/** Same-origin fetch: the session lives in an httpOnly cookie, so the browser
 *  never handles a token and XSS cannot read one. */

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly body?: unknown) {
    super(code);
  }
}

/** Thrown when the request never reached a server — no connection, DNS failure,
 *  the tunnel dropped mid-flight. Distinct from an `ApiError`, which means the
 *  server answered and said no: one is worth retrying later, the other is not. */
export class Offline extends Error {
  constructor() { super('offline'); }
}

export async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new Offline();
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string })?.error ?? `http_${res.status}`, data);
  return data as T;
}

/** `navigator.onLine` only ever proves the negative: false means there is
 *  certainly no connection, true means the network card is up and says nothing
 *  about whether the server is reachable. Used to skip a request that is bound to
 *  fail, never to conclude that one will succeed. */
export const surelyOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
